import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthContext from '../../contexts/auth-context.js';
import { getGatesAPI } from './dummyLiveData.js';
import moment from 'moment';
import { getMostRecentSeriesAndGame } from '../Retail/helper/helper_functions.js';

const GatesLocationController = ({ children }) => {
  const location = useLocation();
  const { state } = location;
  const id = state?.id;
  const currentLocation = state?.currentLocation;
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  // Same as util's getQueuesAPI unless dummy data mode is on (local dev only)
  const getQueuesAPI = getGatesAPI(currentLocation);

  const [loading, setLoading] = useState(true);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [isToday, setIsToday] = useState(false);
  const [now, setNow] = useState(
    new Date(new Date().toLocaleString('en-US', { timeZone: currentLocation?.timeZone || 'America/New_York' }))
  );
  const lastFetchedGameRef = useRef(null);
  // Only the latest request may update the page (a slow response for an earlier game is dropped)
  const requestIdRef = useRef(0);

  const [games, setGames] = useState([]);
  const [currentSeason, setCurrentSeason] = useState(null);
  const [currentSeries, setCurrentSeries] = useState(null);
  const [currentGame, setCurrentGame] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  const [gatesData, setGatesData] = useState([]);
  // Venue time of the last successful data load (shown as "Updated" on live days)
  const [lastUpdated, setLastUpdated] = useState(null);

  const [autoFetchEnabled, setAutoFetchEnabled] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(
        new Date(new Date().toLocaleString('en-US', { timeZone: currentLocation?.timeZone || 'America/New_York' }))
      );
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, [currentLocation?.timeZone]);

  useEffect(() => {
    getGames();
  }, [currentLocation]);

  useEffect(() => {
    const nowMoment = moment(now);
    const adjustedNow = nowMoment.hour() < 3
      ? nowMoment.subtract(1, "day").startOf("day")
      : nowMoment.startOf("day");
  
    const gameDateMatch =
      currentGame?.date &&
      moment(currentGame.date).startOf("day").isSame(adjustedNow);
  
    setIsToday(gameDateMatch);
    if (gameDateMatch) {
      setAutoFetchEnabled(true);
    } else {
      setAutoFetchEnabled(false);
    }
  }, [currentGame, selectedDate, now]);

  const getGames = () => {
    setGames([])
    
    getQueuesAPI(
      "get-location-current-schedule",
      { 
        venue: authCtx.venue,
        locationId: currentLocation?.id,
        measurement: currentLocation?.measurement,
      },
      (data_, error) => {
        if (error) {
            if (error.status === 401) {
                authCtx.logout();
                navigate("/auth");
            } else {
                setLoading(false);
                alert("Unable to fetch data. Please refresh the page and try again.");
            }
        } else {
          const sortedGames = [...data_].sort((a, b) =>
            moment(b.dates[0].date).valueOf() - moment(a.dates[0].date).valueOf()
          );
          const mostRecentYear = moment(sortedGames[0]?.dates[0]?.date).format("YYYY");

          if (!currentSeason) {
            setCurrentSeason(mostRecentYear);
            const adjustedNow = moment(now).tz(currentLocation?.timeZone || 'America/New_York').subtract(3, 'hours').toDate();

            const { recentSeries, recentGame } = getMostRecentSeriesAndGame(data_, adjustedNow);
            if (recentGame) {
              const recentGameYear = moment(recentGame.date).format("YYYY");
              setCurrentSeason(recentGameYear);
            }
            setCurrentSeries(recentSeries);
            setCurrentGame(recentGame);
            if (!recentGame) setLoading(false);
          }
          setGames(data_);
        }
      },
      authCtx.token
    );
  };

  const getGatesData = useCallback((isBackgroundUpdate = false) => {
    return new Promise((resolve, reject) => {
      if (!currentLocation || !currentGame) {
        reject(new Error('Missing required data'));
        return;
      }

      
      const requestId = ++requestIdRef.current;
      if (!isBackgroundUpdate) {
        setLoading(true);
      } else {
        setBackgroundLoading(true);
      }
      const dateStr = moment(selectedDate).format('YYYY-MM-DD');

      getQueuesAPI(
        "get-gates-location-daily-data",
        { 
          timezone: currentLocation.timeZone,
          gates: currentLocation.gates.map(gate => ({
            ...gate,
            entranceSensorIds: gate.multiSensor?.queue_time?.components?.ema_rate?.sensorIds || []
          })),
          venue: authCtx.venue,
          locationId: currentLocation.id,
          measurement: currentLocation.measurement,
          queueMeasurement: currentLocation.queueMeasurement || "",
          date: dateStr
        },
        (data_, error) => {
          if (requestId !== requestIdRef.current) {
            resolve(null);
            return;
          }
          if (error) {
            if (error.status === 401) {
              authCtx.logout();
              navigate("/auth");
              reject(new Error('unauthorized'));
            } else {
              setGatesData([]);
              // Let selecting the same game again retry
              lastFetchedGameRef.current = null;
              reject(new Error('Failed to fetch gates data'));
            }
          } else {
            const transformedGates = currentLocation.gates.map((gate) => {
              const gateKey = String(gate.gateId);
              return {
                id: gate.gateId,
                label: gate.label,
                imageURL: gate.imageURL || null,
                entranceSensorIds: gate.multiSensor?.queue_time?.components?.ema_rate?.sensorIds || [],
                queueIds: gate.queueIds || [],
                totalExits: data_[gateKey]?.totalExits || 0,
                totalEntrances: data_[gateKey]?.totalEntrances || 0,
                rawData: data_[gateKey]?.data || [],
                queueData: data_[gateKey]?.queueData || []
              };
            });
            
            setGatesData(transformedGates);
            setLastUpdated(new Date(new Date().toLocaleString('en-US', { timeZone: currentLocation.timeZone || 'America/New_York' })));

            resolve({
              locationId: currentLocation.id,
              type: 'gates',
              data: {
                type: 'gate traffic',
                gates: transformedGates
              }
            });
          }

          if (!isBackgroundUpdate) {
            setLoading(false);
          } else {
            setBackgroundLoading(false);
          }
        },
        authCtx.token
      );
    });
  }, [authCtx.token, authCtx.venue, currentLocation, currentGame, navigate, selectedDate]);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (autoFetchEnabled && !loading) {
      intervalRef.current = setInterval(() => {
        getGatesData(true).catch(() => {});
      }, 60000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoFetchEnabled, loading, getGatesData]);

  useEffect(() => {
    if (currentGame && selectedDate) {
      const gameDate = moment(currentGame.date).format('YYYY-MM-DD');
      
      if (lastFetchedGameRef.current !== gameDate) {
        lastFetchedGameRef.current = gameDate;
        getGatesData().catch(() => {});
      }
    }
  }, [currentGame, selectedDate, getGatesData]);

  useEffect(() => {
    if (currentGame) {
      setGatesData([]);
      setSelectedDate(moment(currentGame.date).toDate());
    }
  }, [currentGame]);

  useEffect(() => {
    if (!id) {
      navigate("/locations");
    }
  }, [id, navigate]);

  return children({
    loading,
    backgroundLoading,
    autoFetchEnabled,
    currentLocation,
    id,
    games,
    now,
    currentSeries,
    currentGame,
    currentSeason,
    isToday,
    selectedDate,
    gatesData,
    lastUpdated,
    setCurrentSeason,
    setCurrentGame,
    setCurrentSeries,
    setSelectedDate,
    getGatesData,
    setAutoFetchEnabled,
    authCtx,
    navigate
  });
};

export default GatesLocationController;